import { useMemo, useState } from "react";
import PageBanner from "@/components/feature/PageBanner";
import { SkeletonCard } from "@/components/base/Skeleton";
import EmptyState from "@/components/base/EmptyState";
import { useApi } from "@/hooks/useApi";
import { useNow } from "@/hooks/useNow";
import { useAuth } from "@/components/feature/AuthProvider";
import type { RpsBrief } from "@/lib/types";
import RpsCard from "./components/RpsCard";
import RpsWriteModal from "./components/RpsWriteModal";

const PAST_STEP = 6;

// /rps — 진행중 판 + 지난 결과
export default function RpsPage() {
  const { data, loading, error, reload, serverTs } = useApi<RpsBrief[]>("rps.php");
  const now = useNow();
  const { member, requireLogin } = useAuth();
  const [writeOpen, setWriteOpen] = useState(false);
  const [pastLimit, setPastLimit] = useState(PAST_STEP);

  const elapsed = serverTs ? Math.max(0, now / 1000 - serverTs) : 0;

  // server 가 data 에 «진행중 먼저, 그다음 지난 판» 순으로 내려준다.
  const { open, past } = useMemo(() => {
    const list = data ?? [];
    return {
      open: list.filter((r) => r.state === "open"),
      past: list.filter((r) => r.state !== "open"),
    };
  }, [data]);

  const openWrite = () => {
    if (!member) {
      requireLogin("/rps");
      return;
    }
    setWriteOpen(true);
  };

  const handleCreated = (no?: number) => {
    setWriteOpen(false);
    reload();
    if (no) {
      window.setTimeout(() => {
        document
          .getElementById(`rps-${no}`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 350);
    }
  };

  return (
    <>
      <PageBanner
        title="가위바위보"
        description="회원이 직접 여는 가벼운 클럽 게임입니다. 등록자도 손을 미리 내고, 마감되면 한꺼번에 공개됩니다."
        breadcrumb={[{ label: "가위바위보" }]}
      />

      <section className="mx-auto max-w-6xl px-4 py-10 md:px-6 md:py-12">
        {/* 진행중 */}
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
            진행중
            {!loading && open.length > 0 && (
              <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-sm font-semibold text-secondary-900">
                {open.length}
              </span>
            )}
          </h2>
          <button
            type="button"
            onClick={openWrite}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
          >
            <i className="ri-add-line text-base" aria-hidden="true"></i>
            판 열기
          </button>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : error ? (
          <EmptyState
            icon="ri-error-warning-line"
            title="가위바위보 목록을 불러오지 못했습니다"
            description={error}
            action={
              <button
                type="button"
                onClick={reload}
                className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
              >
                다시 시도
              </button>
            }
          />
        ) : open.length === 0 ? (
          <div className="rounded-xl border border-dashed border-background-300 bg-background-50 px-6 py-14 text-center">
            <span className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-400">
              <i className="ri-gamepad-line" aria-hidden="true"></i>
            </span>
            <p className="font-heading text-base font-semibold text-foreground-800">
              지금 열린 판이 없습니다.
            </p>
            <p className="mt-1.5 text-sm text-foreground-500">
              첫 번째 판을 열어보세요!
            </p>
            <button
              type="button"
              onClick={openWrite}
              className="mt-5 inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
            >
              <i className="ri-add-line text-base" aria-hidden="true"></i>
              판 열기
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {open.map((r) => (
              <RpsCard key={r.no} brief={r} elapsed={elapsed} />
            ))}
          </div>
        )}

        {/* 지난 결과 */}
        <div className="mt-12">
          <h2 className="mb-5 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
            <i className="ri-history-line text-foreground-500" aria-hidden="true"></i>
            지난 결과
            {!loading && past.length > 0 && (
              <span className="font-normal text-sm text-foreground-500">
                ({past.length}건)
              </span>
            )}
          </h2>

          {loading ? (
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          ) : past.length === 0 ? (
            <p className="rounded-xl border border-background-200 bg-background-50 px-5 py-8 text-center text-sm text-foreground-500">
              아직 마감된 판이 없습니다.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                {past.slice(0, pastLimit).map((r) => (
                  <RpsCard key={r.no} brief={r} elapsed={elapsed} />
                ))}
              </div>
              {pastLimit < past.length && (
                <div className="mt-6 flex justify-center">
                  <button
                    type="button"
                    onClick={() => setPastLimit((v) => v + PAST_STEP)}
                    className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-6 py-2.5 text-sm font-semibold text-foreground-700 hover:bg-background-100"
                  >
                    더보기
                    <i className="ri-arrow-down-s-line" aria-hidden="true"></i>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <RpsWriteModal
        open={writeOpen}
        onClose={() => setWriteOpen(false)}
        onCreated={handleCreated}
      />
    </>
  );
}